import { _objectAssign, _objectEntries, _objectKeys, _upperFirst } from '@naturalcycles/js-lib'
import { DataFlag, DataIntensity, Goal, pmpSymptomCategories } from '@naturalcycles/shared'
import { TEMP_PMP_IMG } from '@src/app/cnst/perimenopause.cnst.js'
import { TRACKERS_BY_GOAL } from '@src/app/srv/appSettings.cnst.js'
import { keyExists } from '@src/app/srv/translation.util.js'
import { CheckboxGroupTemplate } from '../_templates/checkbox-group/flow-checkbox-group.page.js'
import { InfoTemplate } from '../_templates/info/flow-info.page.js'
import { SliderGroupTemplate } from '../_templates/slider-group/flow-slider-group.page.js'
import { FlowId, FlowInputData, FlowTemplate } from '../flow.cnst.js'
import { PerimenopauseAssessmentFlowData } from './perimenopause-assessment.service.js'

type InputType = FlowInputData<PerimenopauseAssessmentFlowData>

type GetFlowConfigFn<T> = (input: InputType) => T
type GetInfoScreenFn = (input: InputType) => InfoTemplate | undefined
type PerimenopauseAssessmentInputKey = keyof PerimenopauseAssessmentFlowData

export const cycleSymptoms: GetFlowConfigFn<CheckboxGroupTemplate> = _input => ({
  analyticsPageName: 'PerimenopauseAssessmentCycleSymptomsPage',
  template: FlowTemplate.CHECKBOX,
  title: 'onboarding-pmp-assessment-cycle-title',
  body: 'onboarding-pmp-assessment-cycle-txt',
  saveButton: {
    label: 'txt-next',
    uid: 'onboarding__pmpAssessmentCycle__buttonNext',
  },
  skipButton: {
    uid: 'onboarding__pmpAssessmentCycle__buttonNone',
    label: 'txt-quiz-multiselect-none',
  },
  checkboxes: [
    {
      title: 'txt-pmp-assessment--periodsHeavier',
      name: 'periodsHeavier',
      uid: 'onboarding__pmpAssessment__periodsHeavier',
    },
    {
      title: 'txt-pmp-assessment--periodsLighter',
      name: 'periodsLighter',
      uid: 'onboarding__pmpAssessment__periodsLighter',
    },
    {
      title: 'txt-pmp-assessment--periodsPainful',
      name: 'periodsPainful',
      uid: 'onboarding__pmpAssessment__periodsPainful',
    },
    {
      title: 'txt-pmp-assessment--cycleLonger',
      name: 'cycleLonger',
      uid: 'onboarding__pmpAssessment__cycleLonger',
    },
    {
      title: 'txt-pmp-assessment--cycleShorter',
      name: 'cycleShorter',
      uid: 'onboarding__pmpAssessment__cycleShorter',
    },
    {
      title: 'txt-pmp-assessment--cycleUnpredictable',
      name: 'cycleUnpredictable',
      uid: 'onboarding__pmpAssessment__cycleUnpredictable',
    },
  ],
  sideEffects: (input: InputType) => {
    const { periodsHeavier } = input

    // User has already selected something but goes back
    if (periodsHeavier === undefined) {
      return {
        periodsLighter: undefined,
        periodsPainful: undefined,
        cycleLonger: undefined,
        cycleShorted: undefined,
        cycleUnpredictable: undefined,
      }
    }
    // User selected "None"
    if (periodsHeavier === null) {
      return {
        periodsHeavier: false,
        periodsLighter: false,
        periodsPainful: false,
        cycleLonger: false,
        cycleShorted: false,
        cycleUnpredictable: false,
      }
    }
  },
  transitionScreen: (input: InputType) => {
    if (input.flowId === FlowId.PERIMENOPAUSE_ASSESSMENT) {
      return {
        analyticsPageName: 'PMPAssessmmentStartPage',
        template: FlowTemplate.INFO,
        title: 'reg-done-title-pmp',
        body: 'reg-done-body-pmp',
        img: TEMP_PMP_IMG + 180,
        style: {
          hasBackgroundDots: true,
          isDarkVersion: true,
          largeTitle: true,
          imgPosition: 'top',
        },
        buttons: [
          {
            uid: 'onboarding__PMPAssessmmentStartPage__next',
            label: 'txt-next',
          },
        ],
      }
    }

    return {
      analyticsPageName: 'PMPSymptomsTransitionPage',
      template: FlowTemplate.INFO,
      title: 'txt-perimenopause-symptoms',
      body: 'onboarding-perimenopause-symptoms-info',
      style: {
        hasBackgroundDots: true,
        isDarkVersion: true,
      },
      buttons: [
        {
          uid: 'onboarding__PMPSymptomsTransitionPage__next',
          label: 'txt-next',
        },
      ],
    }
  },
})

export const pmpSymptoms: [
  PerimenopauseAssessmentInputKey,
  GetFlowConfigFn<SliderGroupTemplate>,
][] = _objectEntries(pmpSymptomCategories).map(([key, flags]) => {
  const body =
    key === 'lifestyleSymptoms' || key === 'selfcareSymptoms'
      ? `onboarding-pmp-symptoms-txt-2`
      : `onboarding-pmp-symptoms-txt`
  const inputKey: PerimenopauseAssessmentInputKey = key

  const config: GetFlowConfigFn<SliderGroupTemplate> = _input => ({
    analyticsPageName: `PerimenopauseAssessment${_upperFirst(key)}Page`,
    template: FlowTemplate.SLIDER,
    title: `onboarding-pmp-symptoms-title--${key}`,
    body,
    saveButton: {
      label: 'txt-next',
      uid: `onboarding__pmpAssessment${_upperFirst(key)}__buttonNext`,
    },
    name: key,
    rangeSliders: flags.map(dataFlag => ({
      title: keyExists(`txt-dataflag--${DataFlag[dataFlag]}--long`)
        ? `txt-dataflag--${DataFlag[dataFlag]}--long`
        : `txt-dataflag--${DataFlag[dataFlag]}`,
      dataFlag,
      uid: `onboarding__perimenopauseSymptoms__${DataFlag[dataFlag]}`,
    })),
    sideEffects: (input: InputType) => {
      const dataFlags = input.dataFlags || []
      const dataFlagIntensity = input.dataFlagIntensity || {}

      const symptomsIntensityMap = input[key]

      // clear dataflags & intensity if no symptoms selected
      if (!symptomsIntensityMap) {
        flags.forEach(flag => delete dataFlagIntensity[flag])

        return {
          dataFlags: dataFlags.filter(flag => !flags.includes(flag)),
          dataFlagIntensity,
        }
      }

      const allFlags = _objectAssign(dataFlagIntensity, symptomsIntensityMap)
      const flagsWithIntensity = _objectKeys(allFlags)
        .filter(key => allFlags[key] !== DataIntensity.NONE)
        .map(Number)

      return {
        dataFlags: flagsWithIntensity,
        dataFlagIntensity: allFlags,
      }
    },
  })

  return [inputKey, config]
})

const trackers: GetFlowConfigFn<CheckboxGroupTemplate> = _input => {
  const goal = Goal.PERIMENOPAUSE

  return {
    analyticsPageName: 'TrackersPage',
    template: FlowTemplate.CHECKBOX,
    title: 'onboarding-trackers-title',
    body:
      goal === Goal.PERIMENOPAUSE
        ? 'onboarding-trackers-body-perimenopause'
        : 'onboarding-trackers-body',
    saveButton: {
      uid: 'onboarding__trackers__buttonSave',
      label: 'txt-next',
    },
    checkboxes: TRACKERS_BY_GOAL[goal],
  }
}

// Order of this map is important, it decides which page comes next
export const PERIMENOPAUSE_ASSESSMENT_CONFIG = new Map<
  keyof PerimenopauseAssessmentFlowData,
  GetFlowConfigFn<any>
>([['periodsHeavier', cycleSymptoms], ...pmpSymptoms, ['trackers', trackers]])

/**
 * Map of functions that can return an info screen based on the previous page and data.
 */
export const PERIMENOPAUSE_ASSESSMENT_SCREEN = new Map<
  keyof PerimenopauseAssessmentFlowData,
  GetInfoScreenFn
>([])
